import { fromEvent, filter, map, switchMap, of } from "rxjs";

/**
 * Extracts and processes the content of a table cell.
 * @param {HTMLElement} cellElement - The cell element from which to extract the content.
 * @returns {string|number} - Returns the processed cell content as a number if it's a valid number, 
 *                            otherwise returns the cell content in lowercase as a string.
 */
const getCellTableContent = function (cellElement) {
  const cellText = cellElement?.textContent?.trim() || '';
  if (!cellText) {
    return '';
  }

  // remove symbols of unicode like: $, €, ?... and ',' separator
  const cellNumberText = cellText.replace(/\p{Sc}/gu, '').replace(/,/g, '');
  const cellNumberValue = Number(cellNumberText);

  // toLowerCase to allow compare same entries like test >= Test
  return Number.isNaN(cellNumberValue) ? cellText.toLowerCase() : cellNumberValue;
}

/**
 * Sorts the rows of a table based on the content of a specified cell and returns the sorted rows.
 *
 * @param {number} dirModifier - Determines the sorting direction: 1 for ascending, -1 for descending.
 * @param {HTMLElement[]} rows - An array of row elements (tr) to be sorted.
 * @param {number} cellIndex - The index of the cell used for sorting within the row.
 * @returns {HTMLElement[]} The sorted array of rows based on the specified cell content.
 */
const getSortedRows = function (dirModifier, rows, cellIndex) {
  const cellQuerySelector = `td:nth-child(${cellIndex + 1})`;
  const sortedRows = rows.sort((rowA, rowB) => {
    const cellA = rowA.querySelector(cellQuerySelector);
    const cellB = rowB.querySelector(cellQuerySelector);
    const cellAContent = getCellTableContent(cellA);
    const cellBContent = getCellTableContent(cellB);
    return cellAContent > cellBContent ? (1 * dirModifier) : (-1 * dirModifier);
  });

  return sortedRows;
}

/**
 * Updates the table body with new rows and modifies the header attributes to reflect the current sorting order.
 *
 * @param {HTMLElement} tBody - The tbody element of the table that contains the rows to be updated.
 * @param {HTMLElement} tHead - The thead element of the table, used to access header cells.
 * @param {HTMLElement[]} newRows - An array of new row elements (tr) to be inserted into the tbody.
 * @param {HTMLElement} th - The specific header cell (th) that triggered the sort action.
 * @param {string} sortOrder - The current sorting order, either 'asc' for ascending or 'desc' for descending.
 */
const updateTable = function (tBody, tHead, newRows, th, sortOrder) {
  tBody.innerHTML = '';
  tBody.append(...newRows);
  Array.from(tHead.children).forEach(item => item.removeAttribute('data-sort-order'));
  th.setAttribute('data-sort-order', sortOrder);
}

/**
 * Configures a click event on table headers to sort rows based on column values.
 * Sorts the table in ascending or descending order each time the same column is clicked.
 *
 * @param {HTMLElement} tableContainer - The HTML element containing the table to be sorted.
 * @param {string} tHeadSelector - Selector for the table's header (th) elements.
 * @param {string} tBodySelector - Selector for the table's body (tbody) element.
 */
const setTableClickEvent = function (tableContainer) {
  const table = tableContainer.querySelector('table');
  const tHead = table.tHead.querySelector('tr');
  const tBody = table.tBodies[0];
  const rows = Array.from(tBody.querySelectorAll('tr'));
  const tableContainerClick$ = fromEvent(tableContainer, 'click');
  tableContainerClick$.pipe(
    map((event) => event.target.closest('th')),
    filter(Boolean),
    switchMap((th) => {
      const thIndex = Array.prototype.indexOf.call(tHead.children, th);
      const isAscendingSortOrder = th.dataset.sortOrder === 'asc';
      const dirModifier = !isAscendingSortOrder ? 1 : -1;
      const sortOrder = !isAscendingSortOrder ? 'asc' : 'desc';
      const sortedRows = getSortedRows(dirModifier, rows, thIndex);
      return of({
        sortedRows,
        sortOrder,
        th,
      });
    }),
  ).subscribe(({ sortedRows, sortOrder, th }) => {
    updateTable(tBody, tHead, sortedRows, th, sortOrder)
  })
}

const tableContainers = document.querySelectorAll('[data-table-container]');
tableContainers.forEach((tableContainer) => setTableClickEvent(tableContainer));